import { type EntityType } from 'core/domain/EntityType'
import { type FullMode } from 'core/domain/Mode'
import { createDate } from 'core/utils/date'

import { type BulkRates, type BulkRateAvailability, type BulkRate } from '../domain/BulkRates'
import { type RateStatus } from '../domain/Rate'
import { type Shipper } from '../domain/Shipper'
import { type RawQuote, type RawRateLine, mapApiToQuote, mapApiToRateLine } from './RatesMapper'

export type RawBulkCarrier = {
  name: string
  email: string
  entity_type?: EntityType
  id: number
  dot?: string
  scac?: string
  mc?: string
}

type RawShipper = {
  company_name: string
  email: string
  id: number
  logo_email_url: string | null
  phone_number: string | null
}

type RawBulkRateAvailability = {
  is_bid_available: boolean
  reason: string | null
}

type RawBulkRate = {
  availability: RawBulkRateAvailability
  carrier_notes: string | null
  flat_rate: string | null
  per_mile_rate: number | null
  quote: RawQuote
  status: RateStatus
  uuid: string
  ratelines?: Array<RawRateLine>
  book_now_enabled?: boolean
  submitted_at: string
  created_at: string
}

export type RawBulkRates = {
  all_quotes_expired: boolean
  all_rates_sent: boolean
  carrier: RawBulkCarrier
  carrier_name: string
  carrier_email: string
  mode: FullMode
  number_shipments: number
  rates: Array<RawBulkRate>
  shipper: RawShipper
  soonest_bid_deadline?: string
}

function mapApiToBulkRateAvailability(
  rawBulkRateAvailability: RawBulkRateAvailability
): BulkRateAvailability {
  return {
    isBidAvailable: rawBulkRateAvailability.is_bid_available,
    reason: rawBulkRateAvailability.reason || undefined,
  }
}

function mapApiToBulkRate(rawBulkRate: RawBulkRate): BulkRate {
  const ratelines = (rawBulkRate.ratelines ?? []).map(mapApiToRateLine)

  return {
    availability: mapApiToBulkRateAvailability(rawBulkRate.availability),
    carrierNotes: rawBulkRate.carrier_notes ?? '',
    flatRate: rawBulkRate.flat_rate,
    perMileRate: rawBulkRate.per_mile_rate,
    quote: mapApiToQuote(rawBulkRate.quote),
    status: rawBulkRate.status,
    uuid: rawBulkRate.uuid,
    bookNowEnabled: rawBulkRate.book_now_enabled ?? false,
    ratelines,
    submittedAt: createDate(rawBulkRate.submitted_at) || undefined,
    createdAt: new Date(rawBulkRate.created_at),
  }
}

function mapApiToShipperInformation(rawShipper: RawShipper): Shipper {
  return {
    email: rawShipper.email,
    id: rawShipper.id,
    logoUrl: rawShipper.logo_email_url || undefined,
    companyName: rawShipper.company_name,
    phoneNumber: rawShipper.phone_number || undefined,
  }
}

function mapApiToBulkCarrier(carrier: RawBulkCarrier) {
  return {
    name: carrier.name,
    email: carrier.email,
    entityType: carrier?.entity_type,
    id: carrier.id,
    dot: carrier?.dot,
    scac: carrier?.scac,
    mc: carrier?.mc,
  }
}

export function mapApiToBulkRates(rawBulkRates: RawBulkRates): BulkRates {
  return {
    allQuotesExpired: rawBulkRates.all_quotes_expired,
    allRatesSent: rawBulkRates.all_rates_sent,
    carrier: mapApiToBulkCarrier(rawBulkRates.carrier),
    carrierName: rawBulkRates.carrier_name,
    carrierEmail: rawBulkRates.carrier_email,
    mode: rawBulkRates.mode,
    numberShipments: rawBulkRates.number_shipments,
    rates: rawBulkRates.rates.map(mapApiToBulkRate),
    shipper: mapApiToShipperInformation(rawBulkRates.shipper),
    soonestBidDeadline: createDate(rawBulkRates.soonest_bid_deadline),
  }
}
